import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Divider,
  FormControlLabel,
  Switch,
  Stack,
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Facility, FacilityFormData } from '@/constants/types';
import { facilityService } from '@/services';
import toast from 'react-hot-toast';
import { CustomDrawer } from '@/components/common/CustomDrawer';
import { Chip } from '@mui/material';

interface TimeSlot {
  from: Moment | null;
  to: Moment | null;
}

interface FacilityDrawerProps {
  open: boolean;
  onClose: () => void;
  facility?: Facility | null;
  onSuccess: () => void;
}

const validationSchema = Yup.object({
  name: Yup.string().required('Facility name is required').trim(),
  remarks: Yup.string(),
  isActive: Yup.boolean().required('Status is required'),
  availableSlots: Yup.array().min(1, 'At least one time slot is required'),
  nonAvailableDates: Yup.array().min(1, 'At least one date is required'),
});

export const FacilityDrawer: React.FC<FacilityDrawerProps> = ({
  open,
  onClose,
  facility,
  onSuccess,
}) => {
  const isEditMode = !!facility;

  // Time slots state
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([{ from: null, to: null }]);

  // Non-available dates state
  const [selectedDate, setSelectedDate] = useState<Moment | null>(null);

  // Formik setup
  const formik = useFormik<FacilityFormData>({
    initialValues: {
      name: '',
      remarks: '',
      isActive: true,
      availableSlots: [],
      nonAvailableDates: [],
    },
    validationSchema,
    onSubmit: async (values) => {
      // Validate time slots
      const incompleteSlots = timeSlots.filter((slot) => !slot.from || !slot.to);
      if (incompleteSlots.length > 0) {
        toast.error('Please complete all time slots or remove incomplete ones');
        return;
      }

      // Check if at least one time slot exists
      if (timeSlots.length === 0 || timeSlots.every((slot) => !slot.from && !slot.to)) {
        toast.error('At least one time slot is required');
        return;
      }

      // Validate each time slot
      for (let i = 0; i < timeSlots.length; i++) {
        const slot = timeSlots[i];
        if (slot.from && slot.to) {
          // Check if from and to are the same
          if (slot.from.format('HH:mm') === slot.to.format('HH:mm')) {
            toast.error('Start time and end time cannot be the same');
            return;
          }

          // Check if from is after to
          if (slot.from.isAfter(slot.to)) {
            toast.error('Start time must be before end time for all slots');
            return;
          }

          // Check minimum 30 minutes difference
          const diffMinutes = slot.to.diff(slot.from, 'minutes');
          if (diffMinutes < 30) {
            toast.error('Minimum 30 minutes difference required between start and end time');
            return;
          }
        }
      }

      // Check for duplicate "from" times
      const fromTimes = timeSlots
        .filter((slot) => slot.from)
        .map((slot) => slot.from?.format('HH:mm'));
      const uniqueFromTimes = new Set(fromTimes);
      if (uniqueFromTimes.size !== fromTimes.length) {
        toast.error('Duplicate start times are not allowed');
        return;
      }

      // Check for duplicate "to" times
      const toTimes = timeSlots
        .filter((slot) => slot.to)
        .map((slot) => slot.to?.format('HH:mm'));
      const uniqueToTimes = new Set(toTimes);
      if (uniqueToTimes.size !== toTimes.length) {
        toast.error('Duplicate end times are not allowed');
        return;
      }

      // Convert time slots to string array
      const availableSlots = timeSlots
        .filter((slot) => slot.from && slot.to)
        .map((slot) => `${slot.from?.format('hh:mm A')} - ${slot.to?.format('hh:mm A')}`);

      const submitData = {
        ...values,
        availableSlots,
      };

      try {
        if (isEditMode && facility) {
          await facilityService.update(facility._id, submitData);
          toast.success('Facility updated successfully');
        } else {
          await facilityService.add(submitData);
          toast.success('Facility added successfully');
        }
        onSuccess();
        handleClose();
      } catch (error: any) {
        toast.error(error.message || 'Failed to save facility');
      }
    },
  });

  // Initialize form when facility changes
  useEffect(() => {
    if (facility && open) {
      formik.setValues({
        name: facility.name,
        remarks: facility.remarks,
        isActive: facility.isActive,
        availableSlots: facility.availableSlots,
        nonAvailableDates: facility.nonAvailableDates,
      });

      // Parse time slots
      const slots = facility.availableSlots.map((slot) => {
        const [fromStr, toStr] = slot.split(' - ');
        return {
          from: fromStr ? moment(fromStr, 'hh:mm A') : null,
          to: toStr ? moment(toStr, 'hh:mm A') : null,
        };
      });
      setTimeSlots(slots.length > 0 ? slots : [{ from: null, to: null }]);
    } else if (!facility && open) {
      formik.resetForm();
      setTimeSlots([{ from: null, to: null }]);
      setSelectedDate(null);
    }
  }, [facility, open]);

  const handleClose = () => {
    formik.resetForm();
    setTimeSlots([{ from: null, to: null }]);
    setSelectedDate(null);
    onClose();
  };

  // Helper function to sync timeSlots with formik
  const syncTimeSlotsWithFormik = (slots: TimeSlot[]) => {
    const availableSlots = slots
      .filter((slot) => slot.from && slot.to)
      .map((slot) => `${slot.from?.format('hh:mm A')} - ${slot.to?.format('hh:mm A')}`);
    formik.setFieldValue('availableSlots', availableSlots);
  };

  const handleAddTimeSlot = () => {
    // Check if all existing slots are filled before allowing a new one
    const hasIncompleteSlots = timeSlots.some((slot) => !slot.from || !slot.to);
    if (hasIncompleteSlots) {
      toast.error('Please complete all existing time slots before adding a new one');
      return;
    }

    // Validate each time slot
    for (let i = 0; i < timeSlots.length; i++) {
      const slot = timeSlots[i];
      if (slot.from && slot.to) {
        // Check if from and to are the same
        if (slot.from.format('HH:mm') === slot.to.format('HH:mm')) {
          toast.error('Start time and end time cannot be the same');
          return;
        }

        // Check if from is after to
        if (slot.from.isAfter(slot.to)) {
          toast.error('Start time must be before end time for all slots');
          return;
        }

        // Check minimum 30 minutes difference
        const diffMinutes = slot.to.diff(slot.from, 'minutes');
        if (diffMinutes < 30) {
          toast.error('Minimum 30 minutes difference required between start and end time');
          return;
        }
      }
    }

    // Check for duplicate "from" times
    const fromTimes = timeSlots
      .filter((slot) => slot.from)
      .map((slot) => slot.from?.format('HH:mm'));
    const uniqueFromTimes = new Set(fromTimes);
    if (uniqueFromTimes.size !== fromTimes.length) {
      toast.error('Duplicate start times are not allowed');
      return;
    }

    // Check for duplicate "to" times
    const toTimes = timeSlots
      .filter((slot) => slot.to)
      .map((slot) => slot.to?.format('HH:mm'));
    const uniqueToTimes = new Set(toTimes);
    if (uniqueToTimes.size !== toTimes.length) {
      toast.error('Duplicate end times are not allowed');
      return;
    }

    const newSlots = [...timeSlots, { from: null, to: null }];
    setTimeSlots(newSlots);
    syncTimeSlotsWithFormik(newSlots);
  };

  const handleRemoveTimeSlot = (index: number) => {
    const newSlots = timeSlots.filter((_, i) => i !== index);
    const finalSlots = newSlots.length > 0 ? newSlots : [{ from: null, to: null }];
    setTimeSlots(finalSlots);
    syncTimeSlotsWithFormik(finalSlots);
  };

  const handleTimeSlotChange = (index: number, field: 'from' | 'to', value: Moment | null) => {
    const newSlots = [...timeSlots];
    newSlots[index][field] = value;
    setTimeSlots(newSlots);
    syncTimeSlotsWithFormik(newSlots);
  };

  const handleAddDate = () => {
    if (selectedDate) {
      const dateStr = selectedDate.format('YYYY-MM-DD');
      if (!formik.values.nonAvailableDates.includes(dateStr)) {
        formik.setFieldValue('nonAvailableDates', [
          ...formik.values.nonAvailableDates,
          dateStr,
        ]);
        setSelectedDate(null);
      } else {
        toast.error('This date has already been added');
      }
    }
  };

  const handleRemoveDate = (date: string) => {
    formik.setFieldValue(
      'nonAvailableDates',
      formik.values.nonAvailableDates.filter((d) => d !== date)
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <CustomDrawer
        open={open}
        onClose={handleClose}
        title={isEditMode ? 'Edit Facility' : 'Add Facility'}
        width={500}
      >
        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={3}>
            <TextField
              label="Facility Name"
              fullWidth
              required
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />

            <TextField
              label="Remarks"
              fullWidth
              multiline
              rows={3}
              name="remarks"
              value={formik.values.remarks}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.isActive}
                  onChange={(e) => formik.setFieldValue('isActive', e.target.checked)}
                  name="isActive"
                />
              }
              label="Active"
            />

            <Divider />

            <Typography variant="subtitle1" fontWeight={600}>
              Available Time Slots
            </Typography>

            {timeSlots.map((slot, index) => (
              <Box key={index} display="flex" gap={2} alignItems="center">
                <TimePicker
                  label="From"
                  value={slot.from}
                  onChange={(value) => handleTimeSlotChange(index, 'from', value)}
                  slotProps={{ textField: { size: 'small' } }}
                />
                <TimePicker
                  label="To"
                  value={slot.to}
                  onChange={(value) => handleTimeSlotChange(index, 'to', value)}
                  slotProps={{ textField: { size: 'small' } }}
                />
                <IconButton
                  color="error"
                  onClick={() => handleRemoveTimeSlot(index)}
                  disabled={timeSlots.length === 1}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddTimeSlot}
              type="button"
            >
              Add Time Slot
            </Button>

            <Divider />

            <Typography variant="subtitle1" fontWeight={600}>
              Non-Available Dates
            </Typography>

            <Box display="flex" gap={2} alignItems="center">
              <DatePicker
                label="Select Date"
                value={selectedDate}
                onChange={setSelectedDate}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
              <Button
                variant="outlined"
                onClick={handleAddDate}
                disabled={!selectedDate}
                type="button"
              >
                Add
              </Button>
            </Box>

            {formik.values.nonAvailableDates.length > 0 && (
              <Box display="flex" flexWrap="wrap" gap={1}>
                {formik.values.nonAvailableDates.map((date, index) => (
                  <Chip
                    key={index}
                    label={date}
                    onDelete={() => handleRemoveDate(date)}
                    color="error"
                    variant="outlined"
                  />
                ))}
              </Box>
            )}

            <Box display="flex" gap={2} mt={2}>
              <Button variant="contained" fullWidth type="submit">
                {isEditMode ? 'Update' : 'Create'}
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleClose}
                type="button"
              >
                Cancel
              </Button>
            </Box>
          </Stack>
        </form>
      </CustomDrawer>
    </LocalizationProvider>
  );
};
